import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Mic, Square, X } from 'lucide-react';

interface VoiceRecorderProps {
  onRecordingComplete: (file: File) => void;
  onCancel?: () => void;
  maxDurationMs?: number;
}

export default function VoiceRecorder({ onRecordingComplete, onCancel, maxDurationMs = 120000 }: VoiceRecorderProps) {
  const { t } = useTranslation();
  const [isRecording, setIsRecording] = useState(false);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<number | null>(null);
  const startTimeRef = useRef<number>(0);

  const formatDuration = (ms: number): string => {
    const totalSec = Math.floor(ms / 1000);
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    return `${min}:${sec.toString().padStart(2, '0')}`;
  };

  const startRecording = useCallback(async () => {
    try {
      setError(null);
      chunksRef.current = [];

      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
        ? 'audio/webm'
        : 'audio/ogg';

      const recorder = new MediaRecorder(stream, { mimeType });

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        stream.getTracks().forEach(track => track.stop());
        if (timerRef.current) clearInterval(timerRef.current);

        if (chunksRef.current.length > 0) {
          const blob = new Blob(chunksRef.current, { type: mimeType });
          const ext = mimeType.includes('webm') ? 'webm' : 'ogg';
          const file = new File([blob], `recording_${Date.now()}.${ext}`, { type: mimeType });
          onRecordingComplete(file);
        }
        setIsRecording(false);
        setDuration(0);
      };

      mediaRecorderRef.current = recorder;
      recorder.start(250);
      startTimeRef.current = Date.now();
      setIsRecording(true);

      timerRef.current = window.setInterval(() => {
        const elapsed = Date.now() - startTimeRef.current;
        setDuration(elapsed);
        if (elapsed >= maxDurationMs) {
          stopRecording();
        }
      }, 200);
    } catch (err: any) {
      if (err.name === 'NotAllowedError') {
        setError(t('voice.permissionDenied'));
      } else {
        setError(t('voice.recordingError'));
      }
    }
  }, [maxDurationMs, onRecordingComplete, t]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const cancelRecording = useCallback(() => {
    chunksRef.current = [];
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      mediaRecorderRef.current = null;
    }
    if (timerRef.current) clearInterval(timerRef.current);
    setIsRecording(false);
    setDuration(0);
    onCancel?.();
  }, [onCancel]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  if (error) {
    return (
      <div className="flex items-center gap-2 text-danger-500 text-sm">
        <X className="w-4 h-4" />
        <span>{error}</span>
        <button onClick={() => setError(null)} className="underline text-xs">{t('common.retry')}</button>
      </div>
    );
  }

  if (isRecording) {
    return (
      <div className="flex items-center gap-3 bg-danger-50 rounded-lg px-3 py-2">
        <div className="w-3 h-3 bg-danger-500 rounded-full animate-pulse" />
        <span className="text-sm font-mono text-danger-700">{formatDuration(duration)}</span>
        <div className="flex-1" />
        <button onClick={stopRecording} className="p-1.5 bg-danger-600 text-white rounded-full hover:bg-danger-700" title={t('voice.stop')}>
          <Square className="w-4 h-4" />
        </button>
        <button onClick={cancelRecording} className="p-1.5 text-surface-500 hover:text-surface-700" title={t('common.cancel')}>
          <X className="w-4 h-4" />
        </button>
      </div>
    );
  }

  return (
    <button onClick={startRecording} className="flex items-center gap-1 px-3 py-2 text-sm border rounded-lg hover:bg-surface-50 text-surface-600" title={t('voice.record')}>
      <Mic className="w-4 h-4" />
      <span className="hidden sm:inline">{t('voice.record')}</span>
    </button>
  );
}
